import React, { Children } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/Button';
import { MobileHeroSlider } from '../components/MobileHeroSlider';
import { Phone, Calendar, CheckCircle2, ShieldCheck, Truck, ChevronRight } from 'lucide-react';
export function Home() {
  return <div className="bg-white" data-id="element-571">
      <MobileHeroSlider data-id="element-572" />
      {/* HERO */}
      <section className="bg-white py-16 lg:py-24 overflow-hidden" data-id="element-573">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-574">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center" data-id="element-575">
            {/* Left Column - Text Content */}
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.6
          }} className="text-left" data-id="element-576">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold leading-tight text-black tracking-tight mb-6" data-id="element-577">
                Reliable Non-Medical Transportation &{' '}
                <span className="text-primary-600 block mt-2" data-id="element-578">
                  School Runs You Can Trust
                </span>
              </h1>

              <p className="text-lg text-warm-700 mb-8 max-w-xl leading-relaxed" data-id="element-579">
                Safe, professional, and dependable non-medical transportation
                services designed to support everyday mobility needs. At Gozie
                Shipping and Transportation, we focus on providing reliable
                transportation for individuals who need assistance getting
                around—without the need for medical care.
              </p>

              <p className="text-lg text-warm-700 mb-8 max-w-xl leading-relaxed" data-id="element-580">
                From daily school runs for children to transportation for
                seniors and individuals with mobility challenges, our services
                are built on punctuality, safety, and care. Whether it's getting
                to school, appointments, errands, or back home, we ensure every
                ride is comfortable, secure, and stress-free.
              </p>

              <div className="flex flex-col gap-4 max-w-md" data-id="element-581">
                <a href="tel:2404132157" className="w-full" data-id="element-582">
                  <Button size="lg" className="w-full bg-black hover:bg-warm-800 text-white py-4 text-lg justify-center" data-id="element-583">
                    <Phone className="w-5 h-5 mr-3" data-id="element-584" />
                    (240) 413-2157
                  </Button>
                </a>
                <Link to="/booking" className="w-full" data-id="element-585">
                  <Button size="lg" variant="primary" className="w-full py-4 text-lg justify-center" data-id="element-586">
                    Book Transportation
                  </Button>
                </Link>
              </div>
            </motion.div>

            {/* Right Column - Image */}
            <motion.div initial={{
            opacity: 0,
            x: 30
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} className="relative mt-0 lg:mt-0 px-4 pb-8 order-1 lg:order-2" data-id="element-587">
              <div className="relative" data-id="element-588">
                <img src="https://cdn.magicpatterns.com/uploads/kT5XfZgjwAS6LgTyP6B8dW/pexels-mingao-welfarecar-555708794-18247486.jpg" alt="Wheelchair accessible van with deployed seat" className="w-full h-[350px] md:h-[450px] object-cover rounded-tl-[3rem] rounded-br-[3rem] rounded-tr-xl rounded-bl-xl border-4 border-primary-600 shadow-2xl" data-id="element-589" />
                <a href="tel:2404132157" className="absolute -bottom-6 left-1/2 -translate-x-1/2 bg-white border border-warm-200 shadow-lg rounded-lg px-6 py-3 flex items-center gap-3 text-primary-600 font-bold text-lg md:text-xl whitespace-nowrap" data-id="element-590">
                  <Phone className="w-5 h-5 fill-current" data-id="element-591" />
                  (240) 413-2157
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* AFFORDABLE ACCESSIBLE TRANSPORT */}
      <section className="bg-white py-16 lg:py-24 overflow-hidden" data-id="element-592">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-593">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center" data-id="element-594">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="text-left" data-id="element-595">
              <h2 className="text-3xl md:text-4xl font-extrabold text-black mb-4 tracking-tight" data-id="element-596">
                Comprehensive{' '}
                <span className="text-primary-600" data-id="element-597">
                  Transportation & Shipping Services
                </span>
              </h2>

              <p className="text-warm-700 mb-6 text-lg leading-relaxed" data-id="element-598">
                From handicap-accessible rides to local and long-distance
                transportation, plus reliable shipping and delivery — we provide
                the services you need with care and professionalism.
              </p>

              <ul className="space-y-4 mb-8" data-id="element-599">
                {[{
                title: 'Handicap Accessible Transportation',
                desc: 'Safe and comfortable transport for individuals with mobility challenges, including wheelchair accessible rides.'
              }, {
                title: 'Local & Long Distance Travel',
                desc: 'Dependable services for short trips across town or long-distance transportation needs.'
              }, {
                title: 'Shipping & Delivery',
                desc: 'Affordable and reliable shipping for packages, equipment, and important items.'
              }, {
                title: 'Medical Appointments',
                desc: 'Reliable transportation to doctor visits, dialysis, therapy, and hospital discharges.'
              }, {
                title: 'School Runs for Kids',
                desc: 'Safe and reliable daily pick-up and drop-off service for children at affordable rates.'
              }, {
                title: 'Airport Transportation',
                desc: 'Door-to-door service to and from all major airports with full assistance.'
              }].map((item, i) => <li key={i} className="flex items-start" data-id="element-600">
                    <div className="w-1.5 h-1.5 rounded-full bg-primary-600 mt-2.5 mr-3 flex-shrink-0" data-id="element-601"></div>
                    <p className="text-warm-700" data-id="element-602">
                      <strong className="text-black" data-id="element-603">{item.title}:</strong>{' '}
                      {item.desc}
                    </p>
                  </li>)}
              </ul>

              <Link to="/services" className="inline-block w-full max-w-md" data-id="element-604">
                <Button size="lg" variant="primary" className="w-full justify-center py-4 text-lg" data-id="element-605">
                  <CheckCircle2 className="w-5 h-5 mr-2" data-id="element-606" />
                  View All Services
                </Button>
              </Link>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} className="relative px-4 pb-8 hidden lg:block" data-id="element-607">
              <div className="relative" data-id="element-608">
                <img src="https://cdn.magicpatterns.com/uploads/d5zXDrhBCX1EnU4dT6JqNq/pexels-yankrukov-8617515.jpg" alt="Children being picked up for school run" className="w-full h-[350px] md:h-[450px] object-cover rounded-tl-[3rem] rounded-br-[3rem] rounded-tr-xl rounded-bl-xl border-4 border-primary-600 shadow-2xl" data-id="element-609" />
                <a href="tel:2404132157" className="absolute -bottom-6 left-1/2 -translate-x-1/2 bg-white border border-warm-200 shadow-lg rounded-lg px-6 py-3 flex items-center gap-3 text-primary-600 font-bold text-lg md:text-xl whitespace-nowrap" data-id="element-610">
                  <Phone className="w-5 h-5 fill-current" data-id="element-611" />
                  (240) 413-2157
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ACCESSIBLE TAXI SERVICE */}
      <section className="bg-warm-50 py-16 lg:py-24 overflow-hidden" data-id="element-612">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-613">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center" data-id="element-614">
            {/* Left Column - Image */}
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="relative px-4 pb-8" data-id="element-615">
              <div className="relative" data-id="element-616">
                <img src="https://cdn.magicpatterns.com/uploads/mnLLrmg8voM4EStfgj8JEN/pexels-mingao-welfarecar-555708794-24415252.jpg" alt="Wheelchair accessible van with rear ramp deployed" className="w-full h-[350px] md:h-[450px] object-cover rounded-tl-[3rem] rounded-br-[3rem] rounded-tr-xl rounded-bl-xl border-4 border-primary-600 shadow-2xl" data-id="element-617" />
                <a href="tel:2404132157" className="absolute -bottom-6 left-1/2 -translate-x-1/2 bg-white border border-warm-200 shadow-lg rounded-lg px-6 py-3 flex items-center gap-3 text-primary-600 font-bold text-lg md:text-xl whitespace-nowrap" data-id="element-618">
                  <Phone className="w-5 h-5 fill-current" data-id="element-619" />
                  (240) 413-2157
                </a>
              </div>
            </motion.div>

            {/* Right Column - Text Content */}
            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} className="text-left" data-id="element-620">
              <h2 className="text-3xl md:text-4xl font-extrabold text-black mb-4 tracking-tight" data-id="element-621">
                Your Trusted{' '}
                <span className="text-primary-600" data-id="element-622">
                  Affordable Accessible Transport
                </span>
              </h2>

              <p className="text-warm-700 mb-6 text-lg leading-relaxed" data-id="element-623">
                Whether it's a medical appointment, airport transfer, or
                shipping your mobility scooter — our door-through-door service
                ensures you get the best rates without compromising on dignity
                or comfort.
              </p>

              <p className="text-warm-700 mb-6 font-medium" data-id="element-624">
                What makes our affordable service stand out:
              </p>

              <ul className="space-y-4 mb-8" data-id="element-625">
                {[{
                title: 'Budget-Friendly Flat Rates',
                desc: 'Know your fare upfront — no surprises, no hidden charges, just affordable pricing.'
              }, {
                title: 'Passenger & Equipment Shipping',
                desc: 'We safely transport you AND ship your heavy mobility equipment or medical supplies.'
              }, {
                title: 'Door-Through-Door Assistance',
                desc: 'Premium help from your front door to your destination, included in our low rates.'
              }, {
                title: 'Same-Day Booking Available',
                desc: "Need an affordable ride today? Call us and we'll get you there."
              }, {
                title: 'Wheelchair & Stretcher Ready',
                desc: 'Vehicles equipped with ramps, lifts, and secure tie-down systems.'
              }].map((item, i) => <li key={i} className="flex items-start" data-id="element-626">
                    <div className="w-1.5 h-1.5 rounded-full bg-primary-600 mt-2.5 mr-3 flex-shrink-0" data-id="element-627"></div>
                    <p className="text-warm-700" data-id="element-628">
                      <strong className="text-black" data-id="element-629">{item.title}:</strong>{' '}
                      {item.desc}
                    </p>
                  </li>)}
              </ul>

              <Link to="/booking" className="inline-block w-full max-w-md" data-id="element-630">
                <Button size="lg" variant="primary" className="w-full justify-center py-4 text-lg" data-id="element-631">
                  <CheckCircle2 className="w-5 h-5 mr-2" data-id="element-632" />
                  Book Your Ride Now
                </Button>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* WHY CHOOSE */}
      <section className="py-16 lg:py-24 bg-white" data-id="element-633">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-634">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center" data-id="element-635">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="order-2 lg:order-1" data-id="element-636">
              <h2 className="text-3xl md:text-4xl font-extrabold text-black mb-4 tracking-tight" data-id="element-637">
                Why Choose{' '}
                <span className="text-primary-600" data-id="element-638">
                  Gozie Shipping & Transportation?
                </span>
              </h2>

              <p className="text-warm-700 mb-6 text-lg leading-relaxed" data-id="element-639">
                We believe transportation should never be a barrier. That is why
                our services are designed to provide convenience, reliability,
                and affordability for every customer we serve.
              </p>

              <p className="text-warm-700 mb-6 font-medium" data-id="element-640">
                Here is what sets us apart:
              </p>

              <ul className="space-y-4 mb-8" data-id="element-641">
                {[{
                title: 'Reliable Service',
                desc: 'We value punctuality and reliability. Our team works hard to ensure every trip and delivery is completed on time.'
              }, {
                title: 'Affordable Rates',
                desc: 'We believe transportation and mobility services should be accessible to everyone. Our pricing is transparent and affordable.'
              }, {
                title: 'Safe & Accessible Vehicles',
                desc: 'Our vehicles are equipped to support passengers with mobility needs while maintaining the highest safety standards.'
              }, {
                title: 'Friendly & Professional Drivers',
                desc: 'Our drivers are trained to provide respectful, patient, and professional service to all passengers.'
              }, {
                title: '24/7 Availability',
                desc: 'Day or night, our services are just a call away.'
              }].map((item, i) => <li key={i} className="flex items-start" data-id="element-642">
                    <div className="w-1.5 h-1.5 rounded-full bg-primary-600 mt-2.5 mr-3 flex-shrink-0" data-id="element-643"></div>
                    <p className="text-warm-700" data-id="element-644">
                      <strong className="text-black" data-id="element-645">{item.title}:</strong>{' '}
                      {item.desc}
                    </p>
                  </li>)}
              </ul>

              <Link to="/booking" className="inline-block w-full max-w-md" data-id="element-646">
                <Button size="lg" variant="primary" className="w-full justify-center py-4 text-lg" data-id="element-647">
                  <CheckCircle2 className="w-5 h-5 mr-2" data-id="element-648" />
                  Request a Quote
                </Button>
              </Link>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} className="order-1 lg:order-2 relative px-4 pb-8" data-id="element-649">
              <div className="relative" data-id="element-650">
                <img src="https://cdn.magicpatterns.com/uploads/esC1tQpso1NcYtQRmhhfVY/pexels-andromeda99-11935038.jpg" alt="Cargo express aircraft in flight" className="w-full h-[350px] md:h-[450px] object-cover rounded-tl-[3rem] rounded-br-[3rem] rounded-tr-xl rounded-bl-xl border-4 border-primary-600 shadow-2xl" data-id="element-651" />
                <a href="tel:2404132157" className="absolute -bottom-6 left-1/2 -translate-x-1/2 bg-white border border-warm-200 shadow-lg rounded-lg px-6 py-3 flex items-center gap-3 text-primary-600 font-bold text-lg md:text-xl whitespace-nowrap" data-id="element-652">
                  <Phone className="w-5 h-5 fill-current" data-id="element-653" />
                  (240) 413-2157
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* OUR COMMITMENT TO SAFETY */}
      <section className="py-16 lg:py-24 bg-warm-50" data-id="element-654">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-655">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center" data-id="element-656">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} className="order-1 lg:order-1 relative px-4 pb-8" data-id="element-657">
              <div className="relative" data-id="element-658">
                <img src="https://cdn.magicpatterns.com/uploads/iinNqoMj17mMAFu9ryV7YF/pexels-davegarcia-35831401.jpg" alt="Driver operating wheelchair lift on accessible van at hospital" className="w-full h-[350px] md:h-[450px] object-cover rounded-tl-[3rem] rounded-br-[3rem] rounded-tr-xl rounded-bl-xl border-4 border-primary-600 shadow-2xl" data-id="element-659" />
                <a href="tel:2404132157" className="absolute -bottom-6 left-1/2 -translate-x-1/2 bg-white border border-warm-200 shadow-lg rounded-lg px-6 py-3 flex items-center gap-3 text-primary-600 font-bold text-lg md:text-xl whitespace-nowrap" data-id="element-660">
                  <Phone className="w-5 h-5 fill-current" data-id="element-661" />
                  (240) 413-2157
                </a>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="order-2 lg:order-2" data-id="element-662">
              <h2 className="text-3xl md:text-4xl font-extrabold text-black mb-4 tracking-tight" data-id="element-663">
                Our Commitment to <span className="text-primary-600" data-id="element-664">You</span>
              </h2>

              <p className="text-warm-700 mb-6 text-lg leading-relaxed" data-id="element-665">
                We understand that transportation is more than just getting from
                one place to another. For many individuals, especially those
                with mobility needs, it represents independence and freedom.
              </p>

              <p className="text-warm-700 mb-6 font-medium" data-id="element-666">
                That is why we focus on:
              </p>

              <ul className="space-y-4 mb-8" data-id="element-667">
                {[{
                title: 'Safe Transportation',
                desc: 'Every ride is handled with the highest safety standards and care for our passengers.'
              }, {
                title: 'Respectful Service',
                desc: 'Our team treats every customer with dignity, patience, and professionalism.'
              }, {
                title: 'Dependable Scheduling',
                desc: 'We understand how important punctuality is, especially for medical appointments.'
              }, {
                title: 'Comfortable Rides',
                desc: 'Well-maintained vehicles designed for passenger comfort and accessibility.'
              }, {
                title: 'Community Focus',
                desc: 'We are committed to supporting our community by improving mobility and independence.'
              }].map((item, i) => <li key={i} className="flex items-start" data-id="element-668">
                    <div className="w-1.5 h-1.5 rounded-full bg-primary-600 mt-2.5 mr-3 flex-shrink-0" data-id="element-669"></div>
                    <p className="text-warm-700" data-id="element-670">
                      <strong className="text-black" data-id="element-671">{item.title}:</strong>{' '}
                      {item.desc}
                    </p>
                  </li>)}
              </ul>

              <Link to="/about" className="inline-block w-full max-w-md" data-id="element-672">
                <Button size="lg" variant="primary" className="w-full justify-center py-4 text-lg" data-id="element-673">
                  <ShieldCheck className="w-5 h-5 mr-2" data-id="element-674" />
                  Learn More About Us
                </Button>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* SERVING OUR COMMUNITY */}
      <section className="py-16 lg:py-24 bg-white" data-id="element-675">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-676">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center" data-id="element-677">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="order-2 lg:order-1" data-id="element-678">
              <h2 className="text-3xl md:text-4xl font-extrabold text-black mb-4 tracking-tight" data-id="element-679">
                Proudly Serving{' '}
                <span className="text-primary-600" data-id="element-680">Our Community</span>
              </h2>

              <p className="text-warm-700 mb-6 text-lg leading-relaxed" data-id="element-681">
                Gozie Shipping and Transportation proudly serves individuals and
                businesses in our community and surrounding areas. Whether you
                need transportation for personal mobility or dependable shipping
                services, we are here to help.
              </p>

              <p className="text-warm-700 mb-6 font-medium" data-id="element-682">
                Our service coverage includes:
              </p>

              <ul className="space-y-4 mb-8" data-id="element-683">
                {[{
                title: 'Local Transportation',
                desc: 'Comprehensive coverage across the metropolitan area for daily transportation needs.'
              }, {
                title: 'Long Distance Service',
                desc: 'Extended service for trips beyond our primary coverage zone.'
              }, {
                title: 'Medical Facility Partners',
                desc: 'Contracted with hospitals, clinics, and rehabilitation centers.'
              }, {
                title: 'Business Shipping',
                desc: 'Reliable shipping support for local businesses and organizations.'
              }, {
                title: 'Flexible Coverage',
                desc: 'Contact us to confirm service availability in your location.'
              }].map((item, i) => <li key={i} className="flex items-start" data-id="element-684">
                    <div className="w-1.5 h-1.5 rounded-full bg-primary-600 mt-2.5 mr-3 flex-shrink-0" data-id="element-685"></div>
                    <p className="text-warm-700" data-id="element-686">
                      <strong className="text-black" data-id="element-687">{item.title}:</strong>{' '}
                      {item.desc}
                    </p>
                  </li>)}
              </ul>

              <Link to="/service-area" className="inline-block w-full max-w-md" data-id="element-688">
                <Button size="lg" variant="primary" className="w-full justify-center py-4 text-lg" data-id="element-689">
                  <CheckCircle2 className="w-5 h-5 mr-2" data-id="element-690" />
                  View Our Service Area
                </Button>
              </Link>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} className="order-1 lg:order-2 relative px-4 pb-8" data-id="element-691">
              <div className="relative" data-id="element-692">
                <img src="https://cdn.magicpatterns.com/uploads/i9KAo5og7WxP5Yh6N43tkq/pexels-mingao-welfarecar-555708794-20007188.jpg" alt="Accessible van with motorized swivel seat deployed" className="w-full h-[350px] md:h-[450px] object-cover rounded-tl-[3rem] rounded-br-[3rem] rounded-tr-xl rounded-bl-xl border-4 border-primary-600 shadow-2xl" data-id="element-693" />
                <a href="tel:2404132157" className="absolute -bottom-6 left-1/2 -translate-x-1/2 bg-white border border-warm-200 shadow-lg rounded-lg px-6 py-3 flex items-center gap-3 text-primary-600 font-bold text-lg md:text-xl whitespace-nowrap" data-id="element-694">
                  <Phone className="w-5 h-5 fill-current" data-id="element-695" />
                  (240) 413-2157
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* BRAND BANNER */}
      <section className="bg-primary-600 py-16" data-id="element-696">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-697">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} data-id="element-698">
            <h2 className="text-3xl md:text-4xl font-extrabold text-white mb-6" data-id="element-699">
              Your Trusted Transportation & Shipping Partner
            </h2>
            <p className="text-primary-50 text-lg leading-relaxed max-w-4xl mx-auto" data-id="element-700">
              At Gozie Shipping and Transportation, our mission is to make
              transportation accessible, reliable, and affordable for everyone.
              We are committed to supporting our community by providing services
              that improve mobility, independence, and convenience.
            </p>
          </motion.div>
        </div>
      </section>

      {/* SERVICE 1: Wheelchair Transportation */}
      <section className="py-16 lg:py-24 bg-white" data-id="element-701">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-702">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center" data-id="element-703">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="order-2 lg:order-1" data-id="element-704">
              <h2 className="text-3xl md:text-4xl font-extrabold text-black mb-6" data-id="element-705">
                Handicap Accessible{' '}
                <span className="text-primary-600" data-id="element-706">Transportation</span>
              </h2>
              <p className="text-warm-700 mb-6 text-lg leading-relaxed" data-id="element-707">
                We provide safe and comfortable transportation for individuals
                with mobility challenges. Our accessible vehicles are designed
                to accommodate wheelchairs and mobility devices while ensuring a
                smooth and secure ride.
              </p>
              <ul className="space-y-4 mb-8" data-id="element-708">
                {['Medical appointment transportation', 'Assisted mobility transport', 'Wheelchair accessible rides', 'Transportation for seniors and individuals with disabilities'].map((item, i) => <li key={i} className="flex items-start" data-id="element-709">
                    <div className="w-1.5 h-1.5 rounded-full bg-primary-600 mt-2.5 mr-3 flex-shrink-0" data-id="element-710"></div>
                    <p className="text-warm-700" data-id="element-711">{item}</p>
                  </li>)}
              </ul>
              <Link to="/services" className="inline-flex items-center text-primary-600 font-bold hover:text-primary-700 mb-8 transition-colors" data-id="element-712">
                Learn More About Accessible Transportation{' '}
                <ChevronRight className="w-5 h-5 ml-1" data-id="element-713" />
              </Link>
              <div className="block" data-id="element-714">
                <Link to="/booking" className="inline-block w-full max-w-md" data-id="element-715">
                  <Button size="lg" variant="primary" className="w-full justify-center py-4 text-lg" data-id="element-716">
                    <CheckCircle2 className="w-5 h-5 mr-2" data-id="element-717" />
                    Book Transportation
                  </Button>
                </Link>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} className="order-1 lg:order-2 relative px-4 pb-8" data-id="element-718">
              <div className="relative" data-id="element-719">
                <img src="https://cdn.magicpatterns.com/uploads/uPKTbKD1wGkbcERKbrDP4Y/pexels-mingao-welfarecar-555708794-18442877.jpg" alt="Wheelchair accessible vehicle with motorized seat" className="w-full h-[300px] md:h-[400px] object-cover rounded-tl-[3rem] rounded-br-[3rem] rounded-tr-xl rounded-bl-xl border-4 border-primary-600 shadow-2xl" data-id="element-720" />
                <a href="tel:2404132157" className="absolute -bottom-6 left-1/2 -translate-x-1/2 bg-white border border-warm-200 shadow-lg rounded-lg px-6 py-3 flex items-center gap-3 text-primary-600 font-bold text-lg md:text-xl whitespace-nowrap" data-id="element-721">
                  <Phone className="w-5 h-5 fill-current" data-id="element-722" />
                  (240) 413-2157
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* SERVICE 2: Airport Wheelchair Assistance */}
      <section className="py-16 lg:py-24 bg-primary-900 text-white" data-id="element-723">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-724">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center" data-id="element-725">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} className="order-1 lg:order-1 relative px-4 pb-8" data-id="element-726">
              <div className="relative" data-id="element-727">
                <img src="https://cdn.magicpatterns.com/uploads/pQinhRGcgbuqjNZav9R65f/pexels-mingao-welfarecar-555708794-19956153.jpg" alt="Wheelchair accessible van with deployed lift ramp" className="w-full h-[300px] md:h-[400px] object-cover rounded-tl-[3rem] rounded-br-[3rem] rounded-tr-xl rounded-bl-xl border-4 border-white shadow-2xl" data-id="element-728" />
                <a href="tel:2404132157" className="absolute -bottom-6 left-1/2 -translate-x-1/2 bg-white border border-warm-200 shadow-lg rounded-lg px-6 py-3 flex items-center gap-3 text-primary-600 font-bold text-lg md:text-xl whitespace-nowrap" data-id="element-729">
                  <Phone className="w-5 h-5 fill-current" data-id="element-730" />
                  (240) 413-2157
                </a>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="order-2 lg:order-2" data-id="element-731">
              <h2 className="text-3xl md:text-4xl font-extrabold text-white mb-6" data-id="element-732">
                Local & Long Distance{' '}
                <span className="text-primary-300" data-id="element-733">Transportation</span>
              </h2>
              <p className="text-primary-50 mb-6 text-lg leading-relaxed" data-id="element-734">
                Whether you need a short trip across town or long-distance
                transportation, we offer dependable services designed to get you
                where you need to go safely and comfortably.
              </p>
              <ul className="space-y-4 mb-8" data-id="element-735">
                {['Private transportation services', 'Airport transportation', 'Event transportation', 'Long-distance travel arrangements'].map((item, i) => <li key={i} className="flex items-start" data-id="element-736">
                    <div className="w-1.5 h-1.5 rounded-full bg-white mt-2.5 mr-3 flex-shrink-0" data-id="element-737"></div>
                    <p className="text-primary-50" data-id="element-738">{item}</p>
                  </li>)}
              </ul>
              <Link to="/services" className="inline-flex items-center text-white font-bold hover:text-primary-200 mb-8 transition-colors" data-id="element-739">
                Learn More About Our Transportation Services{' '}
                <ChevronRight className="w-5 h-5 ml-1" data-id="element-740" />
              </Link>
              <div className="block" data-id="element-741">
                <Link to="/booking" className="inline-block w-full max-w-md" data-id="element-742">
                  <Button size="lg" variant="outline" className="w-full bg-white text-primary-900 border-white hover:bg-warm-100 hover:text-primary-900 justify-center py-4 text-lg" data-id="element-743">
                    <CheckCircle2 className="w-5 h-5 mr-2" data-id="element-744" />
                    Book Transportation
                  </Button>
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* SERVICE 3: NEMT */}
      <section className="py-16 lg:py-24 bg-white" data-id="element-745">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-746">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center" data-id="element-747">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="order-2 lg:order-1" data-id="element-748">
              <h2 className="text-3xl md:text-4xl font-extrabold text-black mb-6" data-id="element-749">
                Shipping & Delivery{' '}
                <span className="text-primary-600 block" data-id="element-750">Services</span>
              </h2>
              <p className="text-warm-700 mb-6 text-lg leading-relaxed" data-id="element-751">
                We provide affordable and reliable shipping services for
                packages, equipment, and important items. Our team ensures every
                delivery is handled with care and professionalism.
              </p>
              <ul className="space-y-4 mb-8" data-id="element-752">
                {['Local package delivery', 'Business shipping support', 'Secure transport of goods', 'Scheduled delivery services'].map((item, i) => <li key={i} className="flex items-start" data-id="element-753">
                    <div className="w-1.5 h-1.5 rounded-full bg-primary-600 mt-2.5 mr-3 flex-shrink-0" data-id="element-754"></div>
                    <p className="text-warm-700" data-id="element-755">{item}</p>
                  </li>)}
              </ul>
              <Link to="/services" className="inline-flex items-center text-primary-600 font-bold hover:text-primary-700 mb-8 transition-colors" data-id="element-756">
                Learn More About Shipping Services{' '}
                <ChevronRight className="w-5 h-5 ml-1" data-id="element-757" />
              </Link>
              <div className="block" data-id="element-758">
                <Link to="/booking" className="inline-block w-full max-w-md" data-id="element-759">
                  <Button size="lg" variant="primary" className="w-full justify-center py-4 text-lg" data-id="element-760">
                    <CheckCircle2 className="w-5 h-5 mr-2" data-id="element-761" />
                    Request a Quote
                  </Button>
                </Link>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} className="order-1 lg:order-2 relative px-4 pb-8" data-id="element-762">
              <div className="relative" data-id="element-763">
                <img src="https://cdn.magicpatterns.com/uploads/urSJUvcKdrT6UkVocdyLED/william-william-NndKt2kF1L4-unsplash.jpg" alt="Container ship at port with cargo containers" className="w-full h-[300px] md:h-[400px] object-cover rounded-tl-[3rem] rounded-br-[3rem] rounded-tr-xl rounded-bl-xl border-4 border-primary-600 shadow-2xl" data-id="element-764" />
                <a href="tel:2404132157" className="absolute -bottom-6 left-1/2 -translate-x-1/2 bg-white border border-warm-200 shadow-lg rounded-lg px-6 py-3 flex items-center gap-3 text-primary-600 font-bold text-lg md:text-xl whitespace-nowrap" data-id="element-765">
                  <Phone className="w-5 h-5 fill-current" data-id="element-766" />
                  (240) 413-2157
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS (Kept exactly as is) */}
      <section className="py-16 lg:py-24 bg-warm-50" data-id="element-767">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-768">
          <div className="text-center mb-14" data-id="element-769">
            <h2 className="text-3xl md:text-4xl font-extrabold text-black mb-3" data-id="element-770">
              How It Works
            </h2>
            <p className="text-warm-600 text-lg" data-id="element-771">
              Three simple steps to your affordable ride or shipment.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-772">
            {[{
            step: '1',
            icon: <Calendar className="w-7 h-7" data-id="element-773" />,
            title: 'Contact Us',
            desc: 'Book online or call our team to schedule transportation or request a shipping quote.'
          }, {
            step: '2',
            icon: <Truck className="w-7 h-7" data-id="element-774" />,
            title: 'We Arrive',
            desc: 'Our professional driver arrives on time to assist you or pick up your shipment.'
          }, {
            step: '3',
            icon: <ShieldCheck className="w-7 h-7" data-id="element-775" />,
            title: 'Safe Delivery',
            desc: 'Enjoy a comfortable ride or reliable delivery to your destination.'
          }].map((item, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.1
          }} className="text-center p-8 rounded-2xl border border-warm-200 bg-white" data-id="element-776">
                <div className="w-12 h-12 mx-auto bg-primary-600 text-white rounded-full flex items-center justify-center font-bold text-lg mb-5" data-id="element-777">
                  {item.step}
                </div>
                <div className="w-14 h-14 mx-auto bg-primary-50 text-primary-600 rounded-xl flex items-center justify-center mb-5" data-id="element-778">
                  {item.icon}
                </div>
                <h3 className="text-xl font-bold text-black mb-2" data-id="element-779">
                  {item.title}
                </h3>
                <p className="text-warm-600" data-id="element-780">{item.desc}</p>
              </motion.div>)}
          </div>

          <div className="mt-10 text-center" data-id="element-781">
            <a href="tel:2404132157" className="inline-flex items-center justify-center px-6 py-3 bg-black hover:bg-warm-800 text-white font-bold rounded-lg transition-colors" data-id="element-782">
              <Phone className="w-5 h-5 mr-2" data-id="element-783" />
              Call (240) 413-2157
            </a>
          </div>
        </div>
      </section>

      {/* BOTTOM CTA (Kept exactly as is) */}
      <section className="py-16 bg-black" data-id="element-784">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-785">
          <h2 className="text-3xl font-extrabold text-white mb-4" data-id="element-786">
            Need Reliable Transportation?
          </h2>
          <p className="text-warm-400 text-lg mb-8" data-id="element-787">
            Whether you need accessible mobility services or affordable shipping
            solutions, Gozie Shipping and Transportation is ready to help.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-788">
            <Link to="/booking" data-id="element-789">
              <Button size="lg" variant="primary" data-id="element-790">
                Book a Ride
              </Button>
            </Link>
            <a href="tel:2404132157" data-id="element-791">
              <Button size="lg" className="!bg-white !text-amber-900 hover:!bg-warm-100 border-2 border-white hover:border-amber-900" data-id="element-792">
                <Phone className="w-5 h-5 mr-2" data-id="element-793" />
                Call Now
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>;
}